import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { NAV_LINKS, CTAS } from './landingContent';
import LandingCtaButton from './shared/LandingCtaButton';
import { SITE_NAME } from '../../config/seoConfig';
import { LANDING_FONTS } from './landingVisual';

const LandingHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const closeDrawer = () => setDrawerOpen(false);

  const isActive = (to) => {
    if (to.includes('#')) {
      return location.pathname === '/landing' && location.hash === to.split('#')[1]
        ? `#${location.hash.slice(1)}` === to.split('/landing')[1]
        : false;
    }
    return location.pathname === to;
  };

  const navLinkSx = (to) => ({
    fontFamily: LANDING_FONTS.sans,
    opacity: isActive(to) ? 1 : 0.7,
    fontWeight: isActive(to) ? 600 : 500,
    textDecoration: 'none',
    color: 'text.primary',
    fontSize: '0.9rem',
    letterSpacing: '-0.01em',
    '&:hover': { opacity: 1, color: 'primary.main' },
  });

  const renderNavLink = (item, onClick) => (
    <Link
      key={item.label}
      component={RouterLink}
      to={item.to}
      color="inherit"
      onClick={onClick}
      sx={navLinkSx(item.to)}
    >
      {item.label}
    </Link>
  );

  const authActions = (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Button
        component={RouterLink}
        to={CTAS.signIn.to}
        color="inherit"
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: LANDING_FONTS.sans,
          color: 'text.secondary',
          '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
        }}
        data-testid="landing-sign-in"
      >
        {CTAS.signIn.label}
      </Button>
      <LandingCtaButton
        to={CTAS.practice.to}
        label={CTAS.practice.label}
        variant={CTAS.practice.variant}
        size="medium"
      />
    </Stack>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontFamily: LANDING_FONTS.sans,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2, py: 0.75, minHeight: 64 }}>
          <Typography
            component={RouterLink}
            to="/landing"
            variant="h6"
            sx={{
              fontFamily: LANDING_FONTS.sans,
              fontWeight: 800,
              color: 'text.primary',
              textDecoration: 'none',
              letterSpacing: '-0.03em',
              fontSize: '1.15rem',
            }}
          >
            {SITE_NAME}
          </Typography>

          {isMobile ? (
            <IconButton
              edge="end"
              aria-label="Open navigation menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {NAV_LINKS.map((item) => renderNavLink(item))}
              {authActions}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: 280, fontFamily: LANDING_FONTS.sans } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton aria-label="Close navigation menu" onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton component={RouterLink} to={item.to} onClick={closeDrawer}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={CTAS.signIn.to}
              onClick={closeDrawer}
              data-testid="landing-sign-in-drawer"
            >
              <ListItemText primary={CTAS.signIn.label} />
            </ListItemButton>
          </ListItem>
        </List>
        <Box sx={{ p: 2, mt: 'auto' }}>
          <Stack spacing={1}>
            <LandingCtaButton
              to={CTAS.practice.to}
              label={CTAS.practice.label}
              variant={CTAS.practice.variant}
              fullWidth
              onClick={closeDrawer}
            />
            <LandingCtaButton
              to={CTAS.interviewer.to}
              label={CTAS.interviewer.label}
              variant={CTAS.interviewer.variant}
              fullWidth
              onClick={closeDrawer}
              data-testid="landing-drawer-become-interviewer"
            />
            <Button
              component={RouterLink}
              to={CTAS.demoAlt.to}
              fullWidth
              sx={{ textTransform: 'none', fontFamily: LANDING_FONTS.sans }}
              onClick={closeDrawer}
            >
              {CTAS.demoAlt.label}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default LandingHeader;
